/**
 * Chips, as the photographed article rather than a drawing of one.
 *
 * ============================ WHAT CHANGED, AND WHY =============================
 * A bet used to be a vector glyph — a bone disc with six oxblood edge spots and the
 * house B in the middle — beside a number. It was the same glyph for five chips and
 * for five thousand, which is the one thing a real chip never is: colour IS the
 * denomination at a table, and a player reads a stack before they read a label.
 *
 * `Poker assets/export/chips/` has seven photoreal colours, each in two forms:
 *   - `top/` a 133px top-down disc, which is what a bet in front of a seat is;
 *   - `pieces-web/` a cap, a slice and a tail, which stack seamlessly to any height
 *     for the side-on view the saloon table uses.
 * `chips.json` carries the piece heights and — added by `scripts/sync-art.mjs` from
 * `export/README.md`, because the ART has no numbers printed on it — the denomination
 * each colour stands for.
 *
 * ## The export name is deliberately unchanged
 *
 * `mark.tsx` re-exports this, so `import { Chip } from "../lib/mark"` still resolves to
 * the one chip — and the vector chip is gone rather than left behind as a second way of
 * drawing the same object. (The classic felt that imported it that way was retired in
 * Phase 9.)
 * ===============================================================================
 */
import CHIPS from "./chips.json";

export type ChipColor = keyof typeof CHIPS.colors;

interface ChipSpec { denom: number; width: number; cap: number; slice: number; tail: number }

const SPECS = CHIPS.colors as Record<ChipColor, ChipSpec>;

/** Ascending by denomination, so "the largest that fits" is a walk backwards. */
const BY_VALUE: readonly (readonly [ChipColor, ChipSpec])[] =
  (Object.entries(SPECS) as [ChipColor, ChipSpec][])
    .sort((a, b) => a[1].denom - b[1].denom);

/**
 * The colour a bet of this size is made of.
 *
 * The largest denomination that does not exceed the amount, which is how a dealer
 * colours up: 5 is a red, 30 is a green, 4,000 is a purple. Below the smallest chip
 * (fractional blinds at a 0.5/1 table) it is a white, because a bet still has to be
 * drawn as something.
 */
export function chipColorFor(value: number): ChipColor {
  let picked: ChipColor = BY_VALUE[0]![0];
  for (const [color, spec] of BY_VALUE) {
    if (value >= spec.denom) picked = color;
  }
  return picked;
}

export const chipDenom = (color: ChipColor): number => SPECS[color].denom;
export const CHIP_COLORS: readonly ChipColor[] = BY_VALUE.map(([c]) => c);

/**
 * One chip, seen from above. What sits in front of a seat.
 *
 * `value` picks the colour; passing `color` names it outright, which is what the
 * contact sheet and the legend do. The image is decorative — the amount beside it is
 * the accessible text, and announcing "red chip" as well would make a screen reader
 * read every bet twice.
 */
export function Chip({ className, value, color }: {
  className?: string;
  value?: number;
  color?: ChipColor;
}) {
  const c = color ?? (value === undefined ? "white" : chipColorFor(value));
  return (
    <img className={`chip-top${className ? ` ${className}` : ""}`}
         src={`/art/chips/top/${c}.webp`} alt="" aria-hidden="true" />
  );
}

/**
 * A stack of N chips, seen from the side.
 *
 * The pieces tile exactly: a cap on top, N-2 identical slices, a tail at the bottom
 * (`export/README.md` records this tested seamless at 2, 5, 12 and 20). One chip is
 * the cap alone. Heights come from `chips.json` in the pieces' own pixels and are
 * scaled together by `width`, so a stack is always in proportion no matter how wide it
 * is drawn.
 *
 * Used by the saloon view in Phase 7; here now because it is part of the kit, and the
 * contact sheet proves it tiles before anything depends on it.
 */
export function ChipStack({ count, value, color, width = 44, cssWidth, className }: {
  count: number;
  value?: number;
  color?: ChipColor;
  /** Rendered width of the stack in CSS pixels; every piece scales from it. */
  width?: number;
  /**
   * Phase 3b: a CSS length instead — `var(--chip)`, a `cqmin` token — so a stack on
   * the felt is sized by the felt like everything else on it. Every piece's height and
   * offset becomes a `calc()` of it, in the pieces' own proportions. `width` is ignored
   * when this is given.
   */
  cssWidth?: string;
  className?: string;
}) {
  const c = color ?? (value === undefined ? "white" : chipColorFor(value));
  const spec = SPECS[c];
  const n = Math.max(0, Math.floor(count));
  if (n === 0) return null;

  const slices = Math.max(0, n - 2);
  /* In the pieces' pixels; the ratio to the piece width is what gets drawn. */
  const capR = spec.cap / spec.width, sliceR = spec.slice / spec.width, tailR = spec.tail / spec.width;
  // One chip is the cap alone; two are cap + tail; more insert slices between them.
  const heightR = n === 1 ? capR : capR + slices * sliceR + tailR;
  const len = (r: number) => cssWidth ? `calc(${cssWidth} * ${r.toFixed(4)})` : `${r * width}px`;
  const w = cssWidth ?? `${width}px`;

  const piece = (name: string, topR: number, hR: number, key: string) => (
    <img key={key} className="chip-piece" src={`/art/chips/pieces/${c}-${name}.png`}
         alt="" aria-hidden="true"
         style={{ top: len(topR), width: w, height: len(hR) }} />
  );

  const parts = [piece("cap", 0, capR, "cap")];
  for (let i = 0; i < slices; i++) {
    parts.push(piece("slice", capR + i * sliceR, sliceR, `s${i}`));
  }
  if (n > 1) parts.push(piece("tail", capR + slices * sliceR, tailR, "tail"));

  return (
    <span className={`chip-stack${className ? ` ${className}` : ""}`}
          style={{ width: w, height: len(heightR) }}
          role="img" aria-label={`${n} ${c} chip${n === 1 ? "" : "s"}`}>
      {parts}
    </span>
  );
}
